import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type MouseEvent,
} from "react";
import { createPortal } from "react-dom";
import styled from "@emotion/styled";
import { Trans } from "react-i18next";
import { ODSButton } from "@telekom-ods/react-ui-kit/ODSButton";
import { useWidgetStore } from "@/state";
import { Avatar } from "@/components";
import { colors } from "@/styling";
import type { TFunction } from "i18next";
import { registerUrl } from "@/utils";
import { Close } from "@/assets";

const ModalBackdrop = styled.div({
  position: "fixed",
  inset: 0,
  zIndex: 2147483000,
  backgroundColor: "rgba(0, 0, 0, 0.28)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  boxSizing: "border-box",
});

const ModalCard = styled.div({
  position: "relative",
  width: "min(444px, calc(100vw - 32px))",
  maxHeight: "calc(100vh - 32px)",
  overflow: "auto",
  borderRadius: 24,
  background:
    "var(--colours-basic-background-card, var(--colours-basic-background))",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.14)",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  zIndex: 2147483001,
});

const ModalHeader = styled.div({
  position: "relative",
  padding: "16px 48px 16px 16px",
  borderBottom: `1px solid ${colors.silver}`,
});

const ModalTitle = styled.h2({
  margin: 0,
  textAlign: "center",
  color: colors.green,
  fontSize: "1.25rem",
  fontWeight: 600,
  lineHeight: 1.3,
});

const PanelCloseButton = styled("button")({
  position: "absolute",
  top: 10,
  right: 10,
  zIndex: 2,
  cursor: "pointer",
  border: 0,
  padding: 6,
  margin: 0,
  width: 36,
  height: 36,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  color: colors.cello,
  outline: "none",

  "&:focus-visible": {
    outline: `2px solid ${colors.comet}`,
    outlineOffset: 2,
    borderRadius: 8,
  },

  "& svg": {
    width: 20,
    height: 20,
    display: "block",
  },
});

const ModalBody = styled.div({
  padding: "16px 24px",
  flex: "1 1 auto",
});

const BodyText = styled.p({
  margin: 0,
  color: colors.cello,
  lineHeight: 1.2,
});

const BodyTextSpaced = styled(BodyText)({
  marginTop: 16,
});

const HighlightSpan = styled.span({
  color: colors.green,
  fontWeight: 700,
});

const InlineTextButton = styled("button")({
  display: "inline",
  margin: 0,
  padding: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  font: "inherit",
  color: colors.green,
  fontWeight: 700,
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
});

const ContinueRow = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginTop: 16,
  padding: "0 24px",
});

const ModalFooter = styled.div({
  textAlign: "center",
  borderTop: `1px solid ${colors.silver}`,
  padding: "16px 24px",
});

interface ChatModalProps {
  t: TFunction;
}

const ChatbotModal = ({ t }: ChatModalProps) => {
  const open = useWidgetStore((s) => s.chatbotModalOpen);
  const status = useWidgetStore((s) => s.chatbotStatus);
  const user = useWidgetStore((s) => s.state.user);
  const actions = useWidgetStore((s) => s.actions);

  const portalContainer = useWidgetStore((s) => s.portalContainer);

  const displayName = user?.firstName ?? user?.guid;
  const telekomIdNumber = user?.username ?? "";

  const avatarUrl = user?.avatarUrl ?? null;

  const titleId = useId();
  const descId = useId();

  const panelRef = useRef<HTMLDivElement | null>(null);

  const handleClose = useCallback(
    (e?: MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      e?.stopPropagation();
      actions.closeChatbotFn(e?.nativeEvent);
    },
    [actions],
  );

  const onPointerDownCapture = useCallback(
    (ev: PointerEvent) => {
      if (!open) return;
      const panel = panelRef.current;
      const path = (ev.composedPath?.() ?? []) as EventTarget[];
      const insidePanel = !!panel && path.includes(panel);
      if (!insidePanel) {
        useWidgetStore.getState().actions.closeChatbotFn();
      }
    },
    [open],
  );

  useEffect(() => {
    if (!open) return;

    window.addEventListener("pointerdown", onPointerDownCapture, true);
    return () =>
      window.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [open, onPointerDownCapture]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        useWidgetStore.getState().actions.closeChatbotFn();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const first = panelRef.current.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    window.requestAnimationFrame(() => first?.focus());
  }, [open]);

  const handleRegisterClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(registerUrl, "_blank", "noopener,noreferrer");
  };

  const handleContinue = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    actions.continueChatbotFn(e.nativeEvent);
  };

  const handleReloginFooter = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    actions.reLoginFn(e.nativeEvent);
  };

  const portalRoot = portalContainer ?? document.body;

  const btnGrow: CSSProperties = {
    flex: 1,
    minWidth: 0,
    boxSizing: "border-box",
  };

  const modal = open ? (
    <ModalBackdrop
      role="presentation"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <ModalCard
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        data-scheme="neutral"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id={titleId}>{t("chatbot.welcome")}</ModalTitle>
          <PanelCloseButton
            type="button"
            onClick={handleClose}
            aria-label={t("dropdown.close")}
          >
            <Close aria-hidden />
          </PanelCloseButton>
        </ModalHeader>

        <ModalBody id={descId}>
          <BodyText>
            <Trans
              i18nKey="chatbot.beginChatConnected"
              values={{ telekomIdNumber }}
              components={[<HighlightSpan key="0" />]}
            />
          </BodyText>

          <BodyTextSpaced>
            <Trans
              i18nKey="chatbot.needConnectionRegistered"
              components={[
                <InlineTextButton
                  key="0"
                  type="button"
                  onClick={handleRegisterClick}
                />,
              ]}
            />
          </BodyTextSpaced>

          <ContinueRow>
            <ODSButton
              autoFocus
              type="button"
              variant="primary"
              size="small"
              textWrap
              label={`${t("chatbot.continueAs")}${displayName ?? ""}`}
              onClick={handleContinue}
              disabled={status === "loading"}
              loading={status === "loading"}
              style={btnGrow}
            />
            <span aria-hidden>
              <Avatar src={avatarUrl} size={28} />
            </span>
          </ContinueRow>
        </ModalBody>

        <ModalFooter>
          <ODSButton
            type="button"
            variant="ghost"
            size="small"
            label={t("dropdown.signInWithAnother")}
            onClick={handleReloginFooter}
          />
        </ModalFooter>
      </ModalCard>
    </ModalBackdrop>
  ) : null;

  return modal ? createPortal(modal, portalRoot) : null;
};

export default ChatbotModal;
